import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { FlaskConical, ShieldCheck, Award, Leaf, Star, Cross } from "lucide-react";

const trustItems = [
  { icon: FlaskConical, label: "Formulado con Ker Clinic", href: "https://kerclinic.com" },
  { icon: ShieldCheck, label: "COFEPRIS Registrado" },
  { icon: Award, label: "≥30% β-Glucanos" },
  { icon: Leaf, label: "100% Vegano" },
  { icon: Star, label: "4.8/5 (500+ reseñas)" },
];

const HeroSection = () => {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source
          src="https://res.cloudinary.com/dbmfmpgop/video/upload/reishi_relax_vid_2_vv0tfp.mp4"
          type="video/mp4"
        />
      </video>

      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 pointer-events-none" />

      <div className="container relative z-10 py-20">
        <div className="max-w-3xl mx-auto text-center">
          {/* Ker Clinic Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex"
          >
            <a
              href="https://kerclinic.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-colors"
            >
              <Cross className="w-4 h-4 text-umbo-gold" />
              <span className="text-sm font-medium text-white">Formulado por Especialistas de Ker Clinic</span>
              <ShieldCheck className="w-4 h-4 text-umbo-gold" />
            </a>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-bold leading-[1.08] text-white">
            Energía, Enfoque y{" "}
            <span className="text-umbo-gold italic font-heading">Bienestar Natural</span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-5 inline-flex"
          >
            <a
              href="https://kerclinic.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-colors"
            >
              <Cross className="w-4 h-4 text-umbo-gold" />
              <span className="text-sm font-medium text-white">Respaldado por Ker Clinic</span>
            </a>
          </motion.div>

          <motion.p
            className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
          >
            Hongos adaptógenos con formulación clínica. Biotecnología mexicana respaldada por Ker Clinic.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24 }}
          >
            <Button
              size="xl"
              className="bg-umbo-gold text-umbo-stone font-semibold rounded-full hover:brightness-110 shadow-lg hover:shadow-xl px-10"
              onClick={() => {
                if ((window as any).fbq) {
                  (window as any).fbq("track", "InitiateCheckout", {
                    content_name: "Hero - Ver Productos",
                    currency: "MXN",
                    value: 0,
                  });
                }
                if ((window as any).gtag) {
                  (window as any).gtag("event", "view_item_list", {
                    item_list_name: "Hero CTA",
                    currency: "MXN",
                  });
                }
                document.getElementById("productos")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Ver Productos
            </Button>
            <Button
              size="xl"
              className="bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 px-10"
              onClick={() => {
                if ((window as any).fbq) {
                  (window as any).fbq("track", "InitiateCheckout", {
                    content_name: "Hero - Cuál es para mí",
                    currency: "MXN",
                    value: 0,
                  });
                }
                if ((window as any).gtag) {
                  (window as any).gtag("event", "view_item_list", {
                    item_list_name: "Hero CTA - Comparativa",
                    currency: "MXN",
                  });
                }
                document.getElementById("comparativa")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              ¿Cuál es para mí?
            </Button>
          </motion.div>

          <motion.div
            className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            {trustItems.map(({ icon: Icon, label, href }) => {
              const content = (
                <span className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span>{label}</span>
                </span>
              );
              return href ? (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center text-white/70 hover:text-white text-xs sm:text-sm transition-colors underline-offset-2 hover:underline">
                  {content}
                </a>
              ) : (
                <div key={label} className="flex items-center text-white/70 text-xs sm:text-sm">
                  {content}
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
